import { useState, useEffect, useCallback } from "react";
import { PurchaseOrder } from "@po-management/types";
import { useApiClient } from "../auth/useApiClient";

export interface AdminPOFilters {
  status?: string;
  creatorOid?: string;
  assignedApproverOid?: string;
  vendorId?: string;
  dateFrom?: string;
  dateTo?: string;
  classification?: string;
}

interface AdminPOsResult {
  items: PurchaseOrder[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  setPage: (page: number) => void;
}

export function useAdminPOs(filters: AdminPOFilters, pageSize = 25): AdminPOsResult {
  const { get } = useApiClient();
  const [items, setItems] = useState<PurchaseOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPOs = useCallback((): void => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    if (filters.status)               params.set("status", filters.status);
    if (filters.creatorOid)           params.set("creatorOid", filters.creatorOid);
    if (filters.assignedApproverOid)  params.set("assignedApproverOid", filters.assignedApproverOid);
    if (filters.vendorId)             params.set("vendorId", filters.vendorId);
    if (filters.dateFrom)             params.set("dateFrom", filters.dateFrom);
    if (filters.dateTo)               params.set("dateTo", filters.dateTo);
    if (filters.classification)       params.set("classification", filters.classification);

    get(`/api/admin/pos?${params.toString()}`)
      .then((res) => res.json() as Promise<{ success: boolean; data: { items: PurchaseOrder[]; total: number; page: number; pageSize: number }; error?: string }>)
      .then((body) => {
        if (body.success) {
          setItems(body.data.items);
          setTotal(body.data.total);
        } else {
          setError(body.error ?? "Failed to load POs");
        }
      })
      .catch(() => setError("Network error"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filters.status, filters.creatorOid, filters.assignedApproverOid, filters.vendorId, filters.dateFrom, filters.dateTo, filters.classification]);

  useEffect(() => {
    fetchPOs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPOs]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.creatorOid, filters.assignedApproverOid, filters.vendorId, filters.dateFrom, filters.dateTo, filters.classification]);

  return { items, total, page, pageSize, loading, error, setPage };
}
